import {
	getLocalizedField,
	type SupportedLocale,
} from '#app/utils/blog-i18n.ts'
import {
	DEFAULT_LANGUAGE,
	SUPPORTED_LANGUAGES,
	localizedPath,
} from '#app/utils/i18n.ts'
import { getShopImgSrc } from '#app/utils/misc.tsx'

const SITE_NAME = 'Open Sourced'
const OG_FALLBACK_IMAGE = '/img/os/hq/logo-mit-text-horizontal.png'

// ── Meta generators ────────────────────────────────────────

type ShopMetaArgs = {
	title: string
	description: string
	siteUrl: string
	lang: string
	path: string
	imageUrl?: string
	imageAlt?: string
}

/**
 * Generate complete meta tags (title, description, OG, Twitter Card,
 * canonical, hreflang) for a shop page.
 */
export function generateShopMeta({
	title,
	description,
	siteUrl,
	lang,
	path,
	imageUrl,
	imageAlt,
}: ShopMetaArgs): Array<Record<string, unknown>> {
	const base = siteUrl.replace(/\/$/, '')
	const url = `${base}${localizedPath(lang, path)}`
	const image = imageUrl ?? `${base}${OG_FALLBACK_IMAGE}`
	const alt = imageAlt ?? SITE_NAME

	const meta: Array<Record<string, unknown>> = [
		{ title },
		{ name: 'description', content: description },
		// Canonical
		{ tagName: 'link', rel: 'canonical', href: url },
		// Open Graph
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: url },
		{ property: 'og:image', content: image },
		{ property: 'og:image:alt', content: alt },
		{ property: 'og:locale', content: 'de_CH' },
		{ property: 'og:site_name', content: SITE_NAME },
		// Twitter Card
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: image },
		{ name: 'twitter:image:alt', content: alt },
	]

	// Hreflang alternates
	for (const l of SUPPORTED_LANGUAGES) {
		meta.push({
			tagName: 'link',
			rel: 'alternate',
			hrefLang: l,
			href: `${base}${localizedPath(l, path)}`,
		})
	}
	meta.push({
		tagName: 'link',
		rel: 'alternate',
		hrefLang: 'x-default',
		href: `${base}${localizedPath(DEFAULT_LANGUAGE, path)}`,
	})

	return meta
}

// ── JSON-LD generators ─────────────────────────────────────

type ProductForJsonLd = {
	nameDe: string
	nameFr: string | null
	nameEn: string | null
	descriptionDe: string | null
	descriptionFr: string | null
	descriptionEn: string | null
	slug: string
	available: boolean
	images: Array<{
		objectKey: string
		altTextDe: string | null
		altTextFr: string | null
		altTextEn: string | null
	}>
	variants: Array<{
		nameDe: string
		nameFr: string | null
		nameEn: string | null
		finalPrice: number
		available: boolean
	}>
	category: {
		slug: string
		nameDe: string
		nameFr: string | null
		nameEn: string | null
	}
}

/**
 * Generate schema.org Product JSON-LD for a product detail page.
 */
export function generateProductJsonLd({
	product,
	locale,
	siteUrl,
}: {
	product: ProductForJsonLd
	locale: SupportedLocale
	siteUrl: string
}) {
	const base = siteUrl.replace(/\/$/, '')
	const name = getLocalizedField(product, 'name', locale)
	const description = getLocalizedField(product, 'description', locale)
	const productUrl = `${base}${localizedPath(locale, `/shop/${product.category.slug}/${product.slug}`)}`

	const images = product.images.map(
		(img) => `${base}${getShopImgSrc(img.objectKey)}`,
	)

	const availableVariants = product.variants.filter((v) => v.available)
	const offers = availableVariants.map((variant) => ({
		'@type': 'Offer' as const,
		name: getLocalizedField(variant, 'name', locale),
		price: variant.finalPrice.toFixed(2),
		priceCurrency: 'CHF',
		availability: 'https://schema.org/InStock',
		url: productUrl,
	}))

	return {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name,
		description: description || undefined,
		url: productUrl,
		image: images.length > 0 ? images : undefined,
		brand: {
			'@type': 'Organization',
			name: 'Open Sourced GmbH',
		},
		offers:
			offers.length === 1
				? offers[0]
				: offers.length > 1
					? { '@type': 'AggregateOffer', offers, priceCurrency: 'CHF',
						lowPrice: Math.min(...availableVariants.map((v) => v.finalPrice)).toFixed(2),
						highPrice: Math.max(...availableVariants.map((v) => v.finalPrice)).toFixed(2),
						offerCount: offers.length,
					  }
					: undefined,
	}
}

/**
 * Generate BreadcrumbList JSON-LD for shop pages.
 * Supports: Shop index, Category, Product detail.
 */
export function generateShopBreadcrumbJsonLd({
	locale,
	siteUrl,
	category,
	product,
}: {
	locale: SupportedLocale
	siteUrl: string
	category?: { slug: string; nameDe: string; nameFr: string | null; nameEn: string | null }
	product?: { slug: string; nameDe: string; nameFr: string | null; nameEn: string | null }
}) {
	const base = siteUrl.replace(/\/$/, '')

	const items: Array<{ name: string; url: string }> = [
		{ name: 'Home', url: `${base}/${locale}` },
		{ name: 'Shop', url: `${base}${localizedPath(locale, '/shop')}` },
	]

	if (category) {
		items.push({
			name: getLocalizedField(category, 'name', locale),
			url: `${base}${localizedPath(locale, `/shop/category/${category.slug}`)}`,
		})
	}

	if (product && category) {
		items.push({
			name: getLocalizedField(product, 'name', locale),
			url: `${base}${localizedPath(locale, `/shop/${category.slug}/${product.slug}`)}`,
		})
	}

	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: items.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	}
}
